import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Business, BusinessSubscription, SubscriptionPlan } from "@shared/schema";
import type { BusinessWithSubscription } from "@/providers/business-provider";
import { useState, useEffect } from "react";
import { useBusinessContext } from "@/providers/business-provider";
import { useLocation } from "wouter";

export interface CreateBusinessData {
  name: string;
  description?: string;
  industry?: string;
  logoUrl?: string;
}

export const useCurrentBusiness = () => {
  const { 
    businesses, 
    activeBusiness, 
    isLoading, 
    activeBusinessId,
    setActiveBusinessId,
    refreshBusinesses
  } = useBusinessContext();
  
  // Switch business function that works with the context provider
  const switchBusiness = (business: BusinessWithSubscription) => {
    // Update local storage
    localStorage.setItem('activeBusinessId', business.id.toString());
    console.log("useCurrentBusiness: Switching business to", business.name, business.id);
    
    // Update the business context
    setActiveBusinessId(business.id);
    
    // Use wouter navigate instead of window.location.href
    const [, navigate] = useLocation();
    navigate(`/dashboard?business=${business.id}&t=${Date.now()}`);
  };
  
  return {
    currentBusiness: activeBusiness,
    businesses,
    isLoading,
    switchBusiness,
    refreshBusinesses
  };
};

export const useBusinesses = () => {
  const queryClient = useQueryClient();
  
  // Use the Business Context as the single source of truth
  const {
    businesses,
    activeBusiness,
    isLoading: isLoadingBusinesses,
    setActiveBusinessId,
    refreshBusinesses
  } = useBusinessContext();
  
  // Create business
  const { mutateAsync: createBusiness, isPending: isPendingCreate } = useMutation({
    mutationFn: async (data: CreateBusinessData) => {
      const response = await apiRequest("POST", "/api/businesses", data);
      return response.json();
    },
    onSuccess: (newBusiness) => {
      console.log("[CreateBusiness] success, new business:", newBusiness.id, newBusiness.name);
      // Backend already set req.session.businessId in the POST handler.
      // Save to localStorage so the provider picks it up on refetch.
      localStorage.setItem('activeBusinessId', newBusiness.id.toString());

      // Refetch the list first — setActiveBusinessId requires the business to be
      // in the businesses array (otherwise it silently rejects).
      refreshBusinesses();

      // Use a short delay so the refetch lands before we try to set active.
      // This is a workaround for the provider's guard in setActiveBusinessId.
      setTimeout(() => {
        setActiveBusinessId(newBusiness.id);
      }, 500);
    },
  });

  // Update business
  const { mutateAsync: updateBusiness, isPending: isPendingUpdate } = useMutation({
    mutationFn: async ({ id, ...data }: { id: number } & Partial<Business>) => {
      const response = await apiRequest("PATCH", `/api/businesses/${id}`, data);
      return response.json();
    },
    onSuccess: (updatedBusiness) => {
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${updatedBusiness.id}`] });
      refreshBusinesses();
    },
  });
  
  // Delete business
  const { mutateAsync: deleteBusiness, isPending: isPendingDelete } = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/businesses/${id}`);
    },
    onSuccess: (_, deletedBusinessId) => {
      // Remove the deleted business from the cache immediately (no network request)
      queryClient.setQueryData<BusinessWithSubscription[]>(["/api/businesses"], (old) =>
        old ? old.filter((b) => b.id !== deletedBusinessId) : []
      );

      // If the deleted business was active, reset selection to first remaining
      if (deletedBusinessId === activeBusiness?.id) {
        const remaining = (businesses ?? []).filter((b) => b.id !== deletedBusinessId);
        if (remaining.length > 0) {
          setActiveBusinessId(remaining[0].id);
        } else {
          localStorage.removeItem('activeBusinessId');
        }
      }

      // Single invalidation — triggers one refetch for active observers
      refreshBusinesses();
    },
  });

  // Admin purge business (hard delete with all related data)
  const { mutateAsync: purgeBusiness, isPending: isPendingPurge } = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest("DELETE", `/api/admin/businesses/${id}/purge?confirm=true`);
      return response.json();
    },
    onSuccess: (_, purgedBusinessId) => {
      queryClient.setQueryData<BusinessWithSubscription[]>(["/api/businesses"], (old) =>
        old ? old.filter((b) => b.id !== purgedBusinessId) : []
      );

      if (purgedBusinessId === activeBusiness?.id) {
        const remaining = (businesses ?? []).filter((b) => b.id !== purgedBusinessId);
        if (remaining.length > 0) {
          setActiveBusinessId(remaining[0].id);
        } else {
          localStorage.removeItem('activeBusinessId');
        }
      }

      refreshBusinesses();
    },
  });

  // Switch business function that defers to the context
  const switchBusiness = (business: BusinessWithSubscription) => {
    // Update local storage
    localStorage.setItem('activeBusinessId', business.id.toString());
    console.log("useBusinesses: Switching to business", business.name, business.id);
    
    // Set the ID in the context
    setActiveBusinessId(business.id);
    
    // Clear related cache
    queryClient.invalidateQueries({ queryKey: [`/api/businesses/${business.id}/transactions`] });
    
    // Use wouter navigate instead of window.location.href
    const [, navigate] = useLocation();
    navigate(`/dashboard?business=${business.id}&t=${Date.now()}`);
  };
  
  return {
    businesses,
    activeBusiness,
    isLoadingBusinesses,
    createBusiness,
    updateBusiness,
    deleteBusiness,
    purgeBusiness,
    switchBusiness,
    isPendingCreate,
    isPendingUpdate,
    isPendingDelete,
    isPendingPurge,
  };
};

export const useSubscriptionPlans = () => {
  // Fetch subscription plans
  const { data: plans = [], isLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/subscription-plans");
      return response.json();
    },
  });
  
  return {
    plans,
    isLoading,
  };
};

export const useBusinessSubscription = (businessId: number | undefined) => {
  const queryClient = useQueryClient();
  
  // Fetch business subscription
  const { 
    data: subscription, 
    isLoading 
  } = useQuery<BusinessSubscription | null>({
    queryKey: ["business-subscription", businessId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/businesses/${businessId}/subscription`);
      return response.json();
    },
    enabled: !!businessId,
  });
  
  // Create subscription
  const { mutateAsync: createSubscription, isPending: isPendingCreate } = useMutation({
    mutationFn: async ({ 
      planId, 
      paymentMethodId 
    }: { 
      planId: number; 
      paymentMethodId?: string;
    }) => {
      if (!businessId) throw new Error("No business selected");
      return apiRequest("POST", `/api/businesses/${businessId}/subscription`, {
        planId,
        paymentMethodId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-subscription", businessId] });
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
    },
  });
  
  // Cancel subscription
  const { mutateAsync: cancelSubscription, isPending: isPendingCancel } = useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("No business selected");
      return apiRequest("DELETE", `/api/businesses/${businessId}/subscription`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-subscription", businessId] });
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
    },
  });
  
  // Update subscription
  const { mutateAsync: updateSubscription, isPending: isPendingUpdate } = useMutation({
    mutationFn: async ({ 
      planId 
    }: { 
      planId: number; 
    }) => {
      if (!businessId) throw new Error("No business selected");
      return apiRequest("PATCH", `/api/businesses/${businessId}/subscription`, {
        planId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-subscription", businessId] });
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
    },
  });
  
  // Create checkout session
  const { mutateAsync: createCheckoutSession, isPending: isPendingCheckout } = useMutation({
    mutationFn: async ({ 
      planId,
      successUrl,
      cancelUrl,
    }: { 
      planId: number;
      successUrl: string;
      cancelUrl: string; 
    }) => {
      if (!businessId) throw new Error("No business selected");
      return apiRequest("POST", `/api/businesses/${businessId}/subscription/checkout`, {
        planId,
        successUrl,
        cancelUrl,
      });
    },
  });
  
  return {
    subscription,
    isLoading,
    createSubscription,
    cancelSubscription,
    updateSubscription,
    createCheckoutSession,
    isPendingCreate,
    isPendingCancel,
    isPendingUpdate,
    isPendingCheckout,
  };
};
